/** The look control's long-press/right-click picker — every AVAILABLE look
 * (firstlight's availableLooks(manifest)) in one small anchored menu, the
 * current look picked out with the same ember/bold treatment StatePicker's
 * rows use for .row.current. Presentational only, same split as every
 * other App.tsx-owned overlay: App owns the open/closed state and the
 * pointerdown/contextmenu wiring on the control itself (see its own
 * comment there), this just renders the list and reports picks/closes back
 * up. Reuses the .anchor-menu visual family (PlanBar's overflow menu)
 * rather than a bottom .sheet — it's a mini-popover anchored beside the
 * control, not a modal.
 *
 * Also carries the "Route line" Colour/Weight picker (Lee's Sep 14 2026
 * "make the route BRIGHT" ask — see routeLook.ts) and the "Map markings"
 * theme (his Sep 16 2026 Bodgers-shakedown ask — see map/mapMarkings.ts) —
 * both live in this same menu rather than a separate control because all
 * three are "how the map looks" choices reached the same way (long-press/
 * right-click the LOOK button). Unlike a MapLook pick, choosing a colour, a
 * weight or a markings theme does NOT close the menu (no onClose call in
 * those handlers) — trying Bold then Heavy, or Ember then Magenta, without
 * reopening the menu each time is the whole point of a live, in-place
 * picker.
 *
 * One asymmetry worth knowing before editing: a Route line pick repaints
 * live (setPaintProperty on the plan layers), while a Map markings pick
 * rebuilds the whole style — the sizes and widths are baked into zoom
 * interpolations at generation time. Both feel instant; only the second
 * one reloads tiles. */
import { useEffect, useRef } from "react";
import { LOOK_LABELS, type MapLook } from "../map/firstlight";
import { MARKINGS, MARKINGS_ORDER, type MapMarkings } from "../map/mapMarkings";
import { ROUTE_COLOR_ORDER, ROUTE_COLORS, ROUTE_WEIGHT_ORDER, ROUTE_WEIGHTS, type RouteLook } from "../plan/routeLook";

export default function LookMenu({
  looks,
  current,
  onPick,
  onClose,
  routeLook,
  onRouteLookChange,
  markings,
  onMarkingsChange,
}: {
  looks: MapLook[];
  current: MapLook;
  onPick: (look: MapLook) => void;
  onClose: () => void;
  routeLook: RouteLook;
  onRouteLookChange: (look: RouteLook) => void;
  markings: MapMarkings;
  onMarkingsChange: (markings: MapMarkings) => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  // Focused the moment the menu opens so the div's own onKeyDown (below)
  // is guaranteed to be in the bubble path for Escape — the exact pattern
  // PlanBar's overflow/closures menus use (their overflowFirstBtnRef
  // comment explains why: without a focus target inside, Escape keydowns
  // on whatever HAD focus and reaches App's window-level draw-mode
  // handler, which would ALSO deselect a point or open the discard-route
  // prompt in the same keystroke).
  const firstBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    firstBtnRef.current?.focus();
  }, []);

  // Outside-click closes it; no backdrop element (this is a mini-popover,
  // not a sheet — see App.tsx's comment on where it sits in the overlay
  // family) — pointerdown, not click, so a press elsewhere closes it as
  // promptly as the map's own contextmenu handling does. Escape is handled
  // on the div itself (not a window listener) so stopPropagation can keep
  // it from cascading into App's draw-mode Escape — see firstBtnRef above.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    addEventListener("pointerdown", onPointerDown);
    return () => {
      removeEventListener("pointerdown", onPointerDown);
    };
  }, [onClose]);

  return (
    <div
      className="anchor-menu look-menu"
      ref={menuRef}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      {looks.map((look, i) => (
        <button
          key={look}
          ref={i === 0 ? firstBtnRef : undefined}
          className={look === current ? "anchor-menu-btn current" : "anchor-menu-btn"}
          onClick={() => onPick(look)}
          aria-current={look === current ? "true" : undefined}
          title={look === current ? `${LOOK_LABELS[look]} (current look)` : `Switch to ${LOOK_LABELS[look]}`}
        >
          {LOOK_LABELS[look]}
        </button>
      ))}

      {/* Named for the same reason "Weight" below is — these three reuse
        * .anchor-menu-btn and would otherwise read as three more map looks
        * continuing the list above. Placed BEFORE "Route line" because it
        * is about the map itself (what the basemap draws), where Route line
        * is about the plan drawn on top of it. */}
      <div className="anchor-menu-title">Map markings</div>
      {MARKINGS_ORDER.map((mk) => {
        const info = MARKINGS[mk];
        const isCurrent = mk === markings;
        return (
          <button
            key={mk}
            type="button"
            className={isCurrent ? "anchor-menu-btn current" : "anchor-menu-btn"}
            aria-pressed={isCurrent}
            title={info.blurb}
            onClick={() => onMarkingsChange(mk)}
          >
            {info.label}
          </button>
        );
      })}

      <div className="anchor-menu-title">Route line</div>
      <div className="route-look-swatches">
        {ROUTE_COLOR_ORDER.map((c) => {
          const info = ROUTE_COLORS[c];
          const isCurrent = c === routeLook.color;
          return (
            <button
              key={c}
              type="button"
              className={isCurrent ? "route-swatch current" : "route-swatch"}
              style={{ backgroundColor: info.hex }}
              aria-pressed={isCurrent}
              // The swatch has no text of its own — a colour IS its label.
              // `title` alone would leave it nameless to a screen reader
              // and hover-only to a sighted keyboard user (title is the
              // weakest accname source there is), so it carries the same
              // explicit aria-label every other icon-only control in this
              // app does (PlanBar, SearchPalette, ShareSheet) and the iOS
              // half's .accessibilityLabel.
              aria-label={info.name}
              title={info.name}
              onClick={() => onRouteLookChange({ ...routeLook, color: c })}
            />
          );
        })}
      </div>
      {/* Named, because these four reuse .anchor-menu-btn and would
        * otherwise read as four more map looks continuing the list above
        * — "Thin / Regular / Bold / Heavy" says nothing about what it is
        * the weight OF. */}
      <div className="anchor-menu-title">Weight</div>
      {ROUTE_WEIGHT_ORDER.map((w) => {
        const info = ROUTE_WEIGHTS[w];
        const isCurrent = w === routeLook.weight;
        return (
          <button
            key={w}
            type="button"
            className={isCurrent ? "anchor-menu-btn current" : "anchor-menu-btn"}
            aria-pressed={isCurrent}
            title={`${info.name} route line (${info.px}px)`}
            onClick={() => onRouteLookChange({ ...routeLook, weight: w })}
          >
            {info.name}
          </button>
        );
      })}
    </div>
  );
}
